import { ReactElement, useContext, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { BENEFICIARY_PASSPORTS_KEY } from 'src/utils/constants/queryKeys';
import { Toast } from '@ds';
import { IToastr } from '@ds/elements/Toast/Toast.types';
import classNames from 'classnames';
import PassportDocumentCenterModal from '../PassportDocumentCenterModal/PassportDocumentCenterModal';
import { IChecklistItem, IChecklistItemGroupRequest } from 'src/shared/types/Documents';
import { updatePassportChecklistGroup, updatePassportForCase } from 'src/shared/services/document-api/Documents';
import { PassportInfo } from 'src/shared/types/Case';
import { BeneDocAdditionalContext } from 'src/shared/providers/DocumentUploadContextProvider';
import { useGetRemoteBeneficiaryId } from 'src/shared/queries/useGetRemoteBeneficiaryId';
import { linkRemotePassportToCase } from 'src/shared/services/fn-api/Passports';
import styles from '../DocumentUploadArea/DocumentUploadArea.module.scss';

interface IUploadAreaProps {
  checklistItem?: IChecklistItem;
  allowFromCobalt?: boolean;
  addChecklistItemGroupForPassport: () => void;
  onRefreshChecklist?: () => void;
  allCasePassports?: boolean;
  allCasePassportIds?: number[];
}

const DefaultUserTypes = [
  'BENEFICIARY',
  'HR_CONTACT',
  'HR_LEAD',
  'NETWORK_PARTNER_L1',
  'NETWORK_PARTNER_L2',
  'NETWORK_PARTNER_L3',
];

const PassportUploadArea = ({
  checklistItem,
  allowFromCobalt,
  addChecklistItemGroupForPassport,
  onRefreshChecklist,
  allCasePassports = false,
  allCasePassportIds = [],
}: IUploadAreaProps): ReactElement => {
  const toastRef = useRef<IToastr>(null);
  const { caseId, beneId } = useContext(BeneDocAdditionalContext);
  const [showDocumentCenterModal, setShowDocumentCenterModal] = useState(false);
  const queryClient = useQueryClient();

  const openDocumentCenterBrowserModal = () => {
    setShowDocumentCenterModal(true);
  };

  const closeDocumentCenterBrowserModal = () => {
    setShowDocumentCenterModal(false);
  };

  const handlePassportUpdate = async (selectedPassportIds: number[], passportInfoDocs: PassportInfo[] | undefined) => {
    closeDocumentCenterBrowserModal();

    if (!caseId) return;

    const checklistItemId = checklistItem?.id || 0;
    const promises: Promise<any>[] = [];
    const selectedLocalPassportIds: number[] = [];

    passportInfoDocs?.forEach((passport) => {
      if (selectedPassportIds.includes(passport.passportId)) {
        const isRemotePassport = passport.isRemote;
        
        if (isRemotePassport) {
          promises.push(
            linkRemotePassportToCase({
              caseId,
              passportId: passport.passportId,
              ...(!allCasePassports && { checklistItemId }),
            })
          );
        } else {
          selectedLocalPassportIds.push(passport.passportId);
        }
      }
    });

    if (allCasePassports) {
      if (selectedLocalPassportIds.length > 0) {
        promises.push(updatePassportForCase({ caseId, passportInfoIds: selectedLocalPassportIds }));
      }
    } else {
      if (selectedLocalPassportIds.length > 0) {
        const selectedPassportInfoDoc = passportInfoDocs?.filter((item) =>
          selectedLocalPassportIds.includes(item.passportId)
        )?.[0]?.documents[0];
        const groupRequest: IChecklistItemGroupRequest = {
          passportInfoIds: selectedLocalPassportIds,
          documentId: selectedPassportInfoDoc?.docId,
          docName: selectedPassportInfoDoc?.docName,
          checklistItemId: checklistItem?.id,
          userTypes: checklistItem?.dependentId ? ['BENEFICIARY'] : DefaultUserTypes,
          roleType: 'INTERNAL',
          dependentId: checklistItem?.dependentId ?? null,
        };
        promises.push(updatePassportChecklistGroup(groupRequest));
      }
    }

    if (promises.length > 0) {
      toastRef.current?.show({ severity: 'info', message: 'Adding passport(s)...' });
      const results = await Promise.allSettled(promises);
      queryClient.refetchQueries({ queryKey: [BENEFICIARY_PASSPORTS_KEY, beneId] });
      toastRef.current?.clear();
      const hasError = results.some((r) => r.status === 'rejected');
      if (hasError) {
        toastRef.current?.show({
          severity: 'error',
          message: 'Some passport(s) could not be added.',
          life: 3000,
          sticky: false,
        });
      } else {
        toastRef.current?.show({
          severity: 'success',
          message: 'Added passport(s) successfully.',
          life: 3000,
          sticky: false,
        });
      }
    }

    if (onRefreshChecklist) {
      onRefreshChecklist();
    }
  };

  return (
    <div className={styles['upload-area-container']} onDragOver={(e) => e.preventDefault()}>
      <div className={styles['bene-upload-section']}>
        {allowFromCobalt && (
          <>
            <div className={classNames(styles['method-container'], styles['separator'])}>
              <div className={styles['icon-wrapper']}>
                <div className={classNames(styles['method-icon'], styles['folder-open-icon'])}></div>
              </div>
              <div className={styles['inner-content']}>
                <div className={styles['method-title']}>{'From Cobalt'}</div>
                <div className={styles['method-desc']}>
                  Link documents you've previously uploaded via the&nbsp;
                  <a
                    id="sb-add-documents-with-document-center-btn"
                    className={styles['redirect-link']}
                    onClick={openDocumentCenterBrowserModal}
                  >
                    Cobalt Document Center
                  </a>
                </div>
              </div>
            </div>
            {showDocumentCenterModal && (
              <PassportDocumentCenterModal
                checklistItem={checklistItem}
                isVisible={showDocumentCenterModal}
                onClose={closeDocumentCenterBrowserModal}
                onUpdate={handlePassportUpdate}
                allCasePassports={allCasePassports}
                allCasePassportIds={allCasePassportIds}
              />
            )}
          </>
        )}
        <div className={styles['method-container']}>
          <div className={styles['icon-wrapper']}>
            <div className={classNames(styles['method-icon'], styles['upload-icon'])}></div>
          </div>
          <div className={styles['inner-content']}>
            <div className={styles['method-title']}>From Device</div>
            <div className={styles['method-desc']}>
              Drag and drop to upload or&nbsp;
              <a
                id="sb-browse-documents-from-computer-btn"
                className={styles['redirect-link']}
                onClick={() => {
                  addChecklistItemGroupForPassport();
                }}
              >
                click to upload from your device
              </a>
            </div>
          </div>
        </div>
      </div>
      <div className={styles['agreement-text']}>
        By adding a document, you agree to share it with your immigration provider, network provider, and employer.
      </div>
      <Toast ref={toastRef} />
    </div>
  );
};

export default PassportUploadArea;
